// CartPage.tsx (الجزء المحدث فقط)

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import InfoIcon from "@mui/icons-material/Info";
import StoreIcon from "@mui/icons-material/Store";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningIcon from "@mui/icons-material/Warning";
import DiscountIcon from "@mui/icons-material/Discount";
import { useNotification } from "../../context/NotificationContext";
import { DeliverySetting, useCart } from "../../context/CartContext";
import { useShopContext } from "../../context/ShopContext";
import CheckoutModal from "./CheckoutModal";
import StoresWithDelivery from "./StoresWithDelivery";
import StoresWithoutDelivery from "./StoresWithoutDelivery";
import CouponSection from "./CouponSection";
import { Product } from "../../types";
import BillPreview from "./BillPreview";

/* ---- Constants ---- */
const FEE_PERCENTAGE = 0.00;
const FIXED_FEE = 0;
const TAX_RATE = 0.0;

/* ---- Helper Types ---- */
interface CartItem {
  productId: number;
  branchId: number;
  name: string;
  image?: string;
  price: number;
  product?: Product;
  quantity: number;
}

interface Store {
  id: number;
  name: string;
  theme?: {
    logo?: string;
    background_image_desktop?: string;
  };
}

/* ---- Helpers ---- */
const currency = (v: number | string): string => Number(v || 0).toFixed(2);

function calculateFees(
  amount: number,
  feePercentage = FEE_PERCENTAGE,
  fixedFee = FIXED_FEE,
  taxRate = TAX_RATE
) {
  const feeBase = amount * feePercentage + fixedFee;
  const feeTax = feeBase * taxRate;
  const totalFee = feeBase + feeTax;
  return { feeBase, feeTax, totalFee };
}

const CartPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { notify } = useNotification();
  const {
    cartItems,
    updateQuantity,
    removeItem,
    checkout,
    loading,
    fetchStoreDeliverySettings,
    validateCoupon,
    currentCoupon,
    couponDiscount,
    removeCoupon,
    getDiscountDetails,
  } = useCart();
  const { branches, stores } = useShopContext();

  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [storeDeliveryData, setStoreDeliveryData] = useState<
    Record<number, DeliverySetting[]>
  >({});
  const [selectedCities, setSelectedCities] = useState<
    Record<number, DeliverySetting | null>
  >({});
  const [isLoadingDeliveries, setIsLoadingDeliveries] = useState(false);
  
  // حالة للتحقق من اكتمال اختيار المدن
  const [isCitiesSelected, setIsCitiesSelected] = useState(false);

  // تحديد اتجاه الصفحة بناءً على اللغة
  const isRTL = i18n.language === "ar";
  const directionClass = isRTL ? "rtl" : "ltr";
  const textAlignClass = isRTL ? "text-right" : "text-left";

  /* ---- Determine Stores in Cart ---- */
  const storeItemsMap = useMemo(() => {
    const map: Record<number, CartItem[]> = {};
    for (const item of cartItems as CartItem[]) {
      console.log(item);
      
      const branch = branches.find((b) => b.id === item.branchId);
      const storeId = branch ? Number(branch.store) : 0;
      map[storeId] = map[storeId] || [];
      map[storeId].push(item);
    }
    return map;
  }, [cartItems]);

  const storeIdsInCart = Object.keys(storeItemsMap).map((id) => Number(id));

  /* ---- Fetch Delivery Settings ---- */
  useEffect(() => {
    const fetchAllDeliveries = async () => {
      if (!storeIdsInCart.length) return;
      setIsLoadingDeliveries(true);

      try {
        const results = await Promise.all(
          storeIdsInCart.map(async (storeId) => {
            try {
              const data = await fetchStoreDeliverySettings(storeId);
              return { storeId, data: data || [] };
            } catch (err) {
              console.error(
                `Error fetching delivery for store ${storeId}:`,
                err
              );
              return { storeId, data: [] };
            }
          })
        );

        const formatted: Record<number, DeliverySetting[]> = {};
        results.forEach(({ storeId, data }) => {
          formatted[storeId] = data;
        });
        setStoreDeliveryData(formatted);
      } finally {
        setIsLoadingDeliveries(false);
      }
    };

    fetchAllDeliveries();
  }, []);

  /* ---- Classify Stores into With and Without Delivery ---- */
  const { storesWithDelivery, storesWithoutDelivery } = useMemo<{
    storesWithDelivery: number[];
    storesWithoutDelivery: number[];
  }>(() => {
    const withDelivery: number[] = [];
    const withoutDelivery: number[] = [];

    storeIdsInCart.forEach((storeId: number) => {
      const deliveryOptions = storeDeliveryData[storeId] || [];
      if (deliveryOptions.length > 0) {
        withDelivery.push(storeId);
      } else {
        withoutDelivery.push(storeId);
      }
    });

    return {
      storesWithDelivery: withDelivery,
      storesWithoutDelivery: withoutDelivery,
    };
  }, [storeIdsInCart, storeDeliveryData]);

  /* ---- Generate Bill for Stores With Delivery ---- */
/* ---- Generate Bill for Stores With Delivery ---- */
const bill = useMemo(() => {
  const result = {
    stores: [] as {
      storeId: number;
      store: Store | null;
      items: CartItem[];
      subtotal: number;        // السعر الفعلي للزبون
      baseSubtotal: number;    // السعر الأساسي للمنتج
      deliveryOption: DeliverySetting | null;
      deliveryFee: number;
      feesObj: ReturnType<typeof calculateFees>;
      totalWithFees: number;
      discountedSubtotal?: number;
      discountAmount?: number;
    }[],
    grand: {
      subtotal: 0,          // قبل أي خصم
      totalFees: 0,
      total: 0,
      discountAmount: 0,
      totalAfterDiscount: 0,
    },
  };

  let totalBaseSubtotalAllStores = 0;

  // جمع السعر الأساسي لكل المتاجر
  for (const storeId of storesWithDelivery) {
    const items = storeItemsMap[storeId] || [];
    totalBaseSubtotalAllStores += items.reduce(
      (sum, it) => sum + (it.product?.base_price ?? it.price) * it.quantity,
      0
    );
  }

  // حساب الفاتورة لكل متجر
  for (const storeId of storesWithDelivery) {
    const items = storeItemsMap[storeId] || [];
    const store = stores.find((s: Store) => Number(s.id) === storeId) || null;

    const baseSubtotal = items.reduce(
      (sum, it) => sum + (it.product?.base_price ?? it.price) * it.quantity,
      0
    );

    const platformSubtotal = items.reduce(
      (sum, it) => sum + it.price * it.quantity,
      0
    );

    const deliveryOption = selectedCities[storeId] || null;
    const deliveryFee = deliveryOption ? Number(deliveryOption.delivery_fee || 0) : 0;

    let finalSubtotal = platformSubtotal;
    let storeDiscountAmount = 0;

    if (couponDiscount && couponDiscount.discountAmount > 0 && totalBaseSubtotalAllStores > 0) {
      // كوبون موجود → نحسب على السعر الأساسي
      const storePercentage = baseSubtotal / totalBaseSubtotalAllStores;
      const storeCouponDiscount = couponDiscount.discountAmount * storePercentage;
      finalSubtotal = Math.max(0, baseSubtotal - storeCouponDiscount);
      storeDiscountAmount = baseSubtotal - finalSubtotal;
    } else {
      // مفيش كوبون → السعر الفعلي من الايتم
      storeDiscountAmount = baseSubtotal - platformSubtotal;
      finalSubtotal = platformSubtotal;
    }

    const feesObj = calculateFees(finalSubtotal);
    const totalWithFees = finalSubtotal + deliveryFee + feesObj.totalFee;

    result.stores.push({
      storeId,
      store,
      items,
      subtotal: finalSubtotal,
      baseSubtotal,
      deliveryOption,
      deliveryFee,
      feesObj,
      totalWithFees,
      discountedSubtotal: storeDiscountAmount > 0 ? finalSubtotal : undefined,
      discountAmount: storeDiscountAmount > 0 ? storeDiscountAmount : undefined,
    });

    result.grand.subtotal += baseSubtotal;
    result.grand.totalFees += feesObj.totalFee + deliveryFee;
    result.grand.total += baseSubtotal + deliveryFee + feesObj.totalFee;
    result.grand.discountAmount += storeDiscountAmount;
    result.grand.totalAfterDiscount += totalWithFees;
  }

  return result;
}, [storeItemsMap, selectedCities, stores, storesWithDelivery, couponDiscount]);

  /* ---- Calculate Total for Stores Without Delivery ---- */
  const storesWithoutDeliveryTotal = useMemo(() => {
    let total = 0;

    storesWithoutDelivery.forEach((storeId: number) => {
      const items = storeItemsMap[storeId] || [];
      const subtotal = items.reduce((s, it) => s + it.price * it.quantity, 0);
      total += subtotal;
    });

    return total;
  }, [storesWithoutDelivery, storeItemsMap]);

  /* ---- Effect to Check Cities Selection ---- */
  useEffect(() => {
    const hasSelectedCities = Object.values(selectedCities).some(
      (city) => city !== null
    );
    setIsCitiesSelected(hasSelectedCities);
  }, [selectedCities]);

  /* ---- UI Handlers ---- */
  const canCheckout = storesWithDelivery.length > 0 && isCitiesSelected;

  const handleCheckoutClick = () => {
    if (!canCheckout) {
      if (storesWithDelivery.length === 0) {
        notify("warning", t("cart.noDeliveryStoresWarning"));
      } else {
        notify("warning", t("cart.selectAtLeastOneCityWarning"));
      }
      return;
    }
    setIsCheckoutModalOpen(true);
  };

  const handleVisitStore = (storeId: number) => {
    notify("info", t("cart.redirectingToStore"));
  };

  /* ---- Coupon Handlers ---- */
  const handleApplyCoupon = async (couponCode: string) => {
    if (!couponCode.trim()) {
      notify("warning", t("cart.enterCouponCode"));
      return;
    }

    try {
      await validateCoupon(couponCode);
    } catch (error) {
      console.error("Failed to apply coupon:", error);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      await removeCoupon();
    } catch (error) {
      console.error("Failed to remove coupon:", error);
    }
  };

  /* ---- Render ---- */
  return (
    <div
      className={`container mx-auto px-6 py-12 ${directionClass}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <h1 className={`text-3xl font-bold mb-8 ${textAlignClass}`}>
        {t("cart.title")}
      </h1>

      {cartItems.length === 0 ? (
        <p className="text-neutral-600 text-center">{t("cart.empty")}</p>
      ) : (
        <>
          {/* 🚚 Stores with Delivery */}
          <StoresWithDelivery
            bill={bill}
            currency={currency}
            isLoadingDeliveries={isLoadingDeliveries}
            isRTL={isRTL}
            removeItem={removeItem}
            selectedCities={selectedCities}
            setSelectedCities={setSelectedCities}
            storeDeliveryData={storeDeliveryData}
            storesWithDelivery={storesWithDelivery}
            updateQuantity={updateQuantity}
            t={t}
            textAlignClass={textAlignClass}
          />

          {/* 💰 Coupon Section - يظهر فقط بعد اختيار المدن */}
          {isCitiesSelected && (
            <CouponSection
              isRTL={isRTL}
              textAlignClass={textAlignClass}
              currentCoupon={currentCoupon}
              couponDiscount={couponDiscount}
              onApplyCoupon={handleApplyCoupon}
              onRemoveCoupon={handleRemoveCoupon}
              t={t}
              isCitiesSelected={isCitiesSelected}
            />
          )}

          {/* 🏬 Stores without Delivery */}
          <StoresWithoutDelivery
            currency={currency}
            handleVisitStore={handleVisitStore}
            isRTL={isRTL}
            removeItem={removeItem}
            storeItemsMap={storeItemsMap}
            stores={stores}
            storesWithoutDelivery={storesWithoutDelivery}
            updateQuantity={updateQuantity}
            t={t}
            textAlignClass={textAlignClass}
          />

          {/* 🧾 Bill Preview */}
{isCitiesSelected && storesWithDelivery.length > 0 && (
  <BillPreview
    bill={bill}
    isRTL={isRTL}
    textAlignClass={textAlignClass}
    currency={currency}
    t={t}
    couponDiscount={couponDiscount}
    currentCoupon={currentCoupon}
  />
)}


          {/* 🛍 Checkout Button */}
          <div
            className={`flex ${isRTL ? "justify-start" : "justify-end"} mt-8`}
          >
            <button
              onClick={handleCheckoutClick}
              disabled={!canCheckout || loading}
              className={`flex items-center bg-primary text-white py-3 px-6 rounded-lg transition-all ${
                isRTL ? "flex-row-reverse" : ""
              } ${
                !canCheckout || loading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-primary-dark hover:shadow-md"
              }`}
            >
              <ShoppingCartCheckoutIcon className={isRTL ? "ml-2" : "mr-2"} />
              {t("cart.checkout")}
            </button>
          </div>

          {/* Modal */}
          <CheckoutModal
            isOpen={isCheckoutModalOpen}
            onClose={() => setIsCheckoutModalOpen(false)}
            onSubmit={checkout}
            isLoading={loading}
            selectedCities={selectedCities}
            currentCoupon={currentCoupon}
            couponDiscount={couponDiscount}
          />
        </>
      )}
    </div>
  );
};

export default CartPage;